import { Link } from 'react-router-dom';
import { isWwwSite } from '../utils/authRedirect';

export const OurStory = () => {
  const isWww = isWwwSite();
  
  return (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
    {/* Navigation */}
    <nav className="relative z-10 flex items-center justify-between p-6 lg:px-8">
      <div className="flex items-center">
        <Link to="/" className="flex items-center">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <span className="ml-3 text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Finderz AI
          </span>
        </Link>
      </div>
      <div className="flex items-center gap-4">
        {isWww ? (
          <>
            <a 
              href="https://app.finderz.ai/login"
              className="text-gray-700 hover:text-blue-600 transition duration-300 font-medium"
            >
              Sign In
            </a>
            <a 
              href="https://app.finderz.ai/register"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl hover:shadow-lg transform hover:scale-105 transition duration-300 font-medium"
            >
              Get Started
            </a>
          </>
        ) : (
          <>
            <Link 
              to="/login" 
              className="text-gray-700 hover:text-blue-600 transition duration-300 font-medium"
            >
              Sign In
            </Link>
            <Link 
              to="/register" 
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl hover:shadow-lg transform hover:scale-105 transition duration-300 font-medium"
            >
              Get Started
            </Link>
          </>
        )}
      </div>
    </nav>

    {/* Hero Section */}
    <div className="relative px-6 lg:px-8 py-12">
      <div className="mx-auto max-w-4xl text-center">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
          Our Story
        </h1>
        <p className="mt-6 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
          From a simple idea to revolutionizing project management with AI
        </p>
      </div>
    </div>

    {/* Story Content */}
    <div className="max-w-4xl mx-auto px-6 pb-12">
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 lg:p-12">
        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">The Beginning</h2>
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              In 2023, our founders were working on a complex infrastructure project that involved coordinating hundreds of team members, managing thousands of tasks, and keeping track of countless moving parts. Traditional project management tools felt clunky, disconnected, and unable to provide the intelligent insights needed to make critical decisions.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              That's when the idea for Finderz AI was born – a project management platform that doesn't just organize your work, but understands it, learns from it, and helps you make better decisions.
            </p>
          </section>

          <section className="mb-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-semibold text-gray-900 mb-6">Our Mission</h2>
                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  To empower teams and organizations with AI-driven project management tools that eliminate complexity, reduce inefficiencies, and unlock human potential.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  We believe that when technology truly understands your work, it can help you achieve things you never thought possible.
                </p>
              </div>
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 rounded-2xl text-white">
                <h3 className="text-2xl font-bold mb-4">Our Values</h3>
                <ul className="space-y-2">
                  <li>✨ Innovation through simplicity</li>
                  <li>🤝 Collaboration over competition</li>
                  <li>🎯 Results-driven approach</li>
                  <li>🛡️ Privacy and security first</li>
                  <li>🌱 Continuous learning and growth</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">The Journey</h2>
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2023: The Spark</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Founded by a team of experienced project managers and AI engineers who were frustrated with existing tools. We started with a simple question: "What if project management software could think?"
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2024-2025: Building the Foundation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Developing our core AI algorithms for data center design and construction. Launching with select partners to refine our technology, ensuring we solve the real challenges of modern infrastructure development.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2026: Scaling Impact</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Expanding globally to transform how data centers are designed and built. Our AI will help create faster, safer, and greener infrastructure, setting new standards for the industry worldwide.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-gray-900 mb-6">Looking Forward</h2>
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              We're just getting started. Our vision is to create a world where project management is no longer a burden, but a catalyst for innovation and success. Where teams can focus on what they do best – creating, building, and solving meaningful problems.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              Every feature we build, every algorithm we train, and every partnership we form is guided by this vision. We're not just building software; we're building the future of work.
            </p>
          </section>

          <section className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-2xl">
            <h2 className="text-3xl font-semibold text-gray-900 mb-4 text-center">Join Our Story</h2>
            <p className="text-gray-700 leading-relaxed text-lg text-center mb-6">
              Whether you're a startup launching your first product or an enterprise managing complex operations, we'd love to be part of your success story.
            </p>
            <div className="flex justify-center">
              {isWww ? (
                <a 
                  href="https://app.finderz.ai/register"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl hover:shadow-lg transform hover:scale-105 transition duration-300 font-medium"
                >
                  Start Your Journey
                </a>
              ) : (
                <Link 
                  to="/register" 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl hover:shadow-lg transform hover:scale-105 transition duration-300 font-medium"
                >
                  Start Your Journey
                </Link>
              )}
            </div>
          </section>
        </div>

        <div className="mt-12 text-center">
          <Link 
            to="/" 
            className="text-gray-600 hover:text-blue-600 transition duration-300 font-medium"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>

    {/* Footer */}
    <footer className="bg-white/80 backdrop-blur-sm mt-12">
      <div className="mx-auto max-w-7xl px-6 py-8 md:flex md:items-center md:justify-between lg:px-8">
        <div className="mt-4 md:order-1 md:mt-0">
          <p className="text-center text-xs leading-5 text-gray-500">
            &copy; 2025 Finderz AI. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  </div>
  );
}; 